import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isGenderedLocale } from 'helpers/locales';
import {
  Avatar,
  GrammaticalGender,
  SupportedLocale,
} from '../../../generated/graphql';
import { AvatarOption, AVATAR_OPTIONS } from '../AvatarSelection/avatarOptions';
import { Stepper } from './Stepper';
import { StepIntro } from './StepIntro';
import { StepDeviceSetup } from './StepDeviceSetup';
import { StepChooseInterviewer } from './StepChooseInterviewer';
import { StepPracticePreview } from './StepPracticePreview';
import { Button } from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import { StepInterviewLanguage } from './StepInterviewLanguage';
import styles from './ConfigWizard.module.scss';

type WizardStep =
  | 'intro'
  | 'device-setup'
  | 'interview-language'
  | 'choose-interviewer'
  | 'practice-preview';

interface ConfigWizardProps {
  isAvatarEnabled: boolean;
  /** Already includes any extra time; 0 when no figure can be derived. */
  estimatedDurationMinutes: number;
  timeModifierPercentage: number;
  remainingDemos: number | null;
  selectedLocales: SupportedLocale[];
  onStartPractice: (
    avatar: AvatarOption,
    locale: SupportedLocale,
    grammaticalGender?: GrammaticalGender
  ) => void;
  onStartInterview: (
    avatar: AvatarOption,
    locale: SupportedLocale,
    grammaticalGender?: GrammaticalGender
  ) => void;
  initialStepIndex?: number;
}

export function ConfigWizard({
  isAvatarEnabled,
  estimatedDurationMinutes,
  timeModifierPercentage,
  remainingDemos,
  selectedLocales,
  onStartPractice,
  onStartInterview,
  initialStepIndex,
}: ConfigWizardProps) {
  const { t } = useTranslation();
  const steps: WizardStep[] = isAvatarEnabled
    ? [
        'intro',
        'device-setup',
        'interview-language',
        'choose-interviewer',
        'practice-preview',
      ]
    : ['intro', 'device-setup', 'interview-language', 'practice-preview'];

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex ?? 0);
  const availableLocales = useMemo(
    () =>
      selectedLocales.length > 0
        ? selectedLocales
        : [SupportedLocale.English],
    [selectedLocales]
  );
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(
    availableLocales[0]
  );
  const [grammaticalGender, setGrammaticalGender] = useState<GrammaticalGender>(
    GrammaticalGender.Masculine
  );
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarOption | null>(
    isAvatarEnabled ? null : AVATAR_OPTIONS.find(o => o.id === Avatar.Spot) ?? null
  );

  useEffect(() => {
    if (initialStepIndex !== undefined) {
      setCurrentStepIndex(initialStepIndex);
    }
  }, [initialStepIndex]);

  useEffect(() => {
    if (!availableLocales.includes(selectedLocale)) {
      setSelectedLocale(availableLocales[0]);
    }
  }, [availableLocales, selectedLocale]);

  const currentStep = steps[currentStepIndex];

  function goNext() {
    setCurrentStepIndex(prev => Math.min(prev + 1, steps.length - 1));
  }

  function goBack() {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }

  // Only sent for gendered locales; the backend clears it for the rest.
  const resolvedGrammaticalGender = isGenderedLocale(selectedLocale)
    ? grammaticalGender
    : undefined;

  function handleStartPractice() {
    if (selectedAvatar) {
      onStartPractice(selectedAvatar, selectedLocale, resolvedGrammaticalGender);
    }
  }

  function handleSkipToInterview() {
    if (selectedAvatar) {
      onStartInterview(
        selectedAvatar,
        selectedLocale,
        resolvedGrammaticalGender
      );
    }
  }

  function getDeviceSetupNextLabel(): string {
    if (isAvatarEnabled) {
      return t('aiInterview.wizard.tryPracticeInterview');
    }
    const name = selectedAvatar?.name ?? 'Spot';
    return t('aiInterview.wizard.meetBeforeInterview', { name });
  }

  function getLanguageStepNextLabel(): string {
    return isAvatarEnabled
      ? t('aiInterview.chooseInterviewer')
      : getDeviceSetupNextLabel();
  }

  function renderStep() {
    switch (currentStep) {
      case 'intro':
        return (
          <StepIntro
            onContinue={goNext}
            estimatedDurationMinutes={estimatedDurationMinutes}
            timeModifierPercentage={timeModifierPercentage}
          />
        );
      case 'device-setup':
        return (
          <StepDeviceSetup
            onContinue={goNext}
            nextStepLabel={t('aiInterview.language.deviceSetupNext')}
          />
        );
      case 'interview-language':
        return (
          <StepInterviewLanguage
            selectedLocales={availableLocales}
            selectedLocale={selectedLocale}
            onLocaleSelect={setSelectedLocale}
            grammaticalGender={grammaticalGender}
            onGrammaticalGenderChange={setGrammaticalGender}
            onContinue={goNext}
            continueLabel={getLanguageStepNextLabel()}
          />
        );
      case 'choose-interviewer':
        return (
          <StepChooseInterviewer
            isAvatarEnabled={isAvatarEnabled}
            selectedAvatar={selectedAvatar}
            onAvatarSelect={setSelectedAvatar}
            onContinue={goNext}
          />
        );
      case 'practice-preview':
        return selectedAvatar ? (
          <StepPracticePreview
            selectedAvatar={selectedAvatar}
            remainingDemos={remainingDemos}
            onStartPractice={handleStartPractice}
            onSkipToInterview={handleSkipToInterview}
          />
        ) : null;
    }
  }

  return (
    <div className={styles.wizard}>
      <Stepper currentStep={currentStepIndex} totalSteps={steps.length} />

      {currentStepIndex > 0 && (
        <Button
          variant="text"
          size="small"
          leftIcon={<Icon icon="left_arrow" className={styles.backIcon} />}
          onClick={goBack}
          className={styles.backButton}
        >
          {t('aiInterview.back')}
        </Button>
      )}

      {renderStep()}
    </div>
  );
}
